import React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import type { McpAccessLevel } from './mcp-consent-safety';

interface AccessOption {
  value: McpAccessLevel;
  titleKey: string;
  detailKey: string;
}

const OPTIONS: AccessOption[] = [
  { value: 'full', titleKey: 'mcpAccess.fullTitle', detailKey: 'mcpAccess.fullDetail' },
  { value: 'read', titleKey: 'mcpAccess.readTitle', detailKey: 'mcpAccess.readDetail' },
];

interface MCPAccessLevelPickerProps {
  value: McpAccessLevel;
  fullAllowed: boolean;
  disabled?: boolean;
  onChange: (value: McpAccessLevel) => void;
}

/**
 * #755: the access level the user grants on the MCP consent screen. Narrow
 * only: when the app asked for read-only, the full option is disabled.
 */
export const MCPAccessLevelPicker: React.FC<MCPAccessLevelPickerProps> = ({
  value,
  fullAllowed,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation(namespace);
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('mcpAccess.legend')}
      </legend>
      {OPTIONS.map((option) => {
        const optionDisabled = option.value === 'full' && !fullAllowed;
        const checked = value === option.value;
        return (
          <label
            key={option.value}
            className={
              'flex items-start gap-3 rounded-xl border px-3.5 py-3 transition-colors ' +
              (optionDisabled
                ? 'cursor-not-allowed border-slate-100 opacity-60 dark:border-slate-800'
                : 'cursor-pointer ' +
                  (checked
                    ? 'border-[#53bdeb] bg-[#53bdeb]/5'
                    : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40'))
            }
          >
            <input
              type="radio"
              name="mcp-access-level"
              value={option.value}
              checked={checked}
              disabled={optionDisabled}
              onChange={() => onChange(option.value)}
              className="mt-1 h-4 w-4 accent-[#259ae9]"
            />
            <span>
              <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                {t(option.titleKey)}
              </span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {t(option.detailKey)}
              </span>
              {optionDisabled && (
                <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">
                  {t('mcpAccess.readOnlyRequested')}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
};

export default MCPAccessLevelPicker;
